import { useState, useEffect, useCallback, useLayoutEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertCircle, CheckCircle, Users, Clock, Loader2, Bot } from 'lucide-react';
import { formatLocalDate } from '@/lib/date';
import { toast } from '@/components/ui/sonner';
import { TeamRole } from '@/types/roles';

interface TeamInvitation {
  id: string;
  teamId: string;
  email: string;
  role: TeamRole;
  status: 'pending' | 'accepted' | 'declined' | 'expired';
  invitedBy: string;
  invitedAt: string;
  expiresAt: string;
  createdAt: string;
  teamName: string;
  inviterName: string;
  inviterEmail: string;
}

const apiCall = async (endpoint: string, options: RequestInit = {}) => {
  const defaultOptions: RequestInit = {
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
  };

  const accessToken = localStorage.getItem('sb-access-token');
  const refreshToken = localStorage.getItem('sb-refresh-token');
  
  if (accessToken && refreshToken) {
    defaultOptions.headers = {
      ...defaultOptions.headers,
      'Authorization': `Bearer ${accessToken}`,
      'X-Refresh-Token': refreshToken
    };
  }

  const API_URL = import.meta.env.VITE_API_URL || 'https://localhost:5001/api';
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'API request failed');
  }

  return response.json();
};

const SophieInvitePage = () => {
  const { inviteId } = useParams<{ inviteId: string }>();
  const navigate = useNavigate();
  
  const [invitation, setInvitation] = useState<TeamInvitation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('signup');
  
  const [signInForm, setSignInForm] = useState({
    email: '',
    password: ''
  });
  
  const [signUpForm, setSignUpForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    organisationName: ''
  });

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.add("sophie-theme");

    return () => {
      root.classList.remove("sophie-theme");
    };
  }, []);

  const loadInvitation = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiCall(`/invites/${inviteId}`);
      const invite = response.data;
      
      const now = new Date();
      const expiresAt = new Date(invite.expiresAt);
      
      if (now > expiresAt) {
        setError('This invitation has expired');
        return;
      }

      if (invite.status !== 'pending') {
        setError('This invitation is no longer valid');
        return;
      }

      setInvitation(invite);
      
      setSignInForm(prev => ({ ...prev, email: invite.email }));
      setSignUpForm(prev => ({ ...prev, email: invite.email }));
      
    } catch (err) {
      console.error('Error loading invitation:', err);
      if (err instanceof Error && err.message.includes('not found')) {
        setError('Invitation not found or has expired');
      } else {
        setError('Failed to load invitation details');
      }
    } finally {
      setIsLoading(false);
    }
  }, [inviteId]);

  useEffect(() => {
    if (inviteId) {
      loadInvitation();
    }
  }, [inviteId, loadInvitation]);

  const signInWithSupabase = async (email: string, password: string) => {
    const response = await apiCall('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    const { accessToken, refreshToken } = response.data;
    localStorage.setItem('sb-access-token', accessToken);
    localStorage.setItem('sb-refresh-token', refreshToken);

    return response.data;
  };

  const signUpWithSupabase = async (email: string, password: string, name: string, organisationName?: string) => {
    const response = await apiCall('/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ 
        email, 
        password, 
        name,
        organisationName: organisationName || undefined,
        teamId: invitation?.teamId,
        inviteId: invitation?.id
      }),
    });

    const { accessToken, refreshToken } = response.data;
    localStorage.setItem('sb-access-token', accessToken);
    localStorage.setItem('sb-refresh-token', refreshToken);

    return response.data;
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!signInForm.email || !signInForm.password) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      setIsProcessing(true);
      await signInWithSupabase(signInForm.email, signInForm.password);
      await acceptInvitation();
    } catch (err) {
      console.error('Sign in error:', err);
      toast.error('Sign in failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!signUpForm.name || !signUpForm.email || !signUpForm.password || !signUpForm.confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (!invitation && !signUpForm.organisationName.trim()) {
      toast.error('Organisation name is required');
      return;
    }

    if (signUpForm.password !== signUpForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (signUpForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      setIsProcessing(true);
      await signUpWithSupabase(signUpForm.email, signUpForm.password, signUpForm.name, signUpForm.organisationName);
      
      toast.success(
        invitation 
          ? 'Account created and invitation accepted! Please check your email to verify your account.'
          : 'Account and organization created! Please check your email to verify your account.'
      );
      
      navigate('/auth/email-verification-pending');
      
    } catch (err) {
      console.error('Sign up error:', err);
      toast.error('Sign up failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setIsProcessing(false);
    }
  };

  const acceptInvitation = async () => {
    try {
      await apiCall(`/invites/${inviteId}/accept`, {
        method: 'POST',
      });

      toast.success('Invitation accepted successfully!');
      navigate('/');
      
    } catch (err) {
      console.error('Error accepting invitation:', err);
      toast.error('Failed to accept invitation: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  const formatDate = (dateString: string) => formatLocalDate(dateString, { year: 'numeric', month: 'long', day: 'numeric' });

  const getDaysUntilExpiry = () => {
    if (!invitation) return 0;
    const now = new Date();
    const expiresAt = new Date(invitation.expiresAt);
    const diffTime = expiresAt.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading invitation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Invalid Invitation</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => navigate('/login')} variant="outline">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!invitation) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Invitation Not Found</h2>
            <p className="text-muted-foreground mb-6">The invitation link appears to be invalid.</p>
            <Button onClick={() => navigate('/login')} variant="outline">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Invitation Details Card */}
        <Card className="card-hover">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-primary flex items-center justify-center mb-4">
              <Bot className="w-10 h-10 text-primary-foreground" />
            </div>
            <CardTitle className="text-2xl">You're Invited!</CardTitle>
            <CardDescription>
              Join <strong>{invitation.teamName}</strong> on HeySophie
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Team:</span>
                <span className="font-medium">{invitation.teamName}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Invited by:</span>
                <span className="font-medium">{invitation.inviterName}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Role:</span>
                <Badge variant={invitation.role === 'admin' ? 'default' : 'secondary'}>
                  {invitation.role}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Expires:</span>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    {getDaysUntilExpiry()} days ({formatDate(invitation.expiresAt)})
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sign In/Sign Up Card */}
        <Card className="card-hover">
          <CardHeader>
            <CardTitle>Join the Team</CardTitle>
            <CardDescription>
              Sign in to your existing account or create a new one to accept the invitation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
                <TabsTrigger value="signin">Sign In</TabsTrigger>
              </TabsList>
              
              <TabsContent value="signin">
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email">Email</Label>
                    <Input
                      id="signin-email"
                      type="email"
                      value={signInForm.email}
                      onChange={(e) => setSignInForm(prev => ({ ...prev, email: e.target.value }))}
                      className="h-12"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signin-password">Password</Label>
                    <Input
                      id="signin-password"
                      type="password"
                      value={signInForm.password}
                      onChange={(e) => setSignInForm(prev => ({ ...prev, password: e.target.value }))}
                      className="h-12"
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full h-12 text-base" disabled={isProcessing}>
                    {isProcessing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Signing In...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Sign In & Accept Invitation
                      </>
                    )}
                  </Button>
                </form>
              </TabsContent>
              
              <TabsContent value="signup">
                <form onSubmit={handleSignUp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-name">Full Name</Label>
                    <Input
                      id="signup-name"
                      type="text"
                      value={signUpForm.name}
                      onChange={(e) => setSignUpForm(prev => ({ ...prev, name: e.target.value }))}
                      className="h-12"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      value={signUpForm.email}
                      onChange={(e) => setSignUpForm(prev => ({ ...prev, email: e.target.value }))}
                      className="h-12"
                      required
                    />
                  </div>
                  {!invitation && (
                    <div className="space-y-2">
                      <Label htmlFor="signup-organisation">Organisation Name</Label>
                      <Input
                        id="signup-organisation"
                        type="text"
                        value={signUpForm.organisationName}
                        onChange={(e) => setSignUpForm(prev => ({ ...prev, organisationName: e.target.value }))}
                        placeholder="Enter your organization name"
                        className="h-12"
                        required
                      />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password</Label>
                    <Input
                      id="signup-password"
                      type="password"
                      value={signUpForm.password}
                      onChange={(e) => setSignUpForm(prev => ({ ...prev, password: e.target.value }))}
                      className="h-12"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-confirm-password">Confirm Password</Label>
                    <Input
                      id="signup-confirm-password"
                      type="password"
                      value={signUpForm.confirmPassword}
                      onChange={(e) => setSignUpForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      className="h-12"
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full h-12 text-base" disabled={isProcessing}>
                    {isProcessing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating Account...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        {invitation ? 'Create Account & Accept Invitation' : 'Create Account & Organization'}
                      </>
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SophieInvitePage;
